<template>
  <div class="popup-body">
    <span class="popup-text">{{ t('general__text__file-change-connection-info') }}</span>
    <div class="popup-buttons">
      <ButtonOutline
        class="button no-height"
        size="small"
        :buttonText="t('general__text__cancel')"
        @click="close"
      />
      <ButtonPrimary
        class="button-primary no-height"
        :class="{ disabled: !isNewCurrentEntity }"
        size="small"
        :disabled="!isNewCurrentEntity"
        :buttonText="t('general__text__file-change-connection')"
        @click="updateConnection"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { useSelectionStore } from '@/pinia/selection'
import { storeToRefs } from 'pinia'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { CoreEntityDto } from '@/interfaces/core.interface'
import { DirectoryItem } from './types'
import { computed, onUnmounted, ref, watch } from 'vue'

type Props = {
  selectedItem?: DirectoryItem
}
const props = withDefaults(defineProps<Props>(), {
  selectedItem: undefined
})

const selectionStore = useSelectionStore()
const generalStore = useGeneralStore()
const { currentEntity } = storeToRefs(selectionStore)
const { t } = useI18n()
const emit = defineEmits<(e: 'updateConnection', entity: CoreEntityDto) => void>()
const isNewCurrentEntity = computed(() => hasNewCurrentEntity.value)
const hasNewCurrentEntity = ref(false)

function close() {
  generalStore.setShowNoFeature(false)
  generalStore.setShowConnectionPopup(false)
}
function updateConnection() {
  if (!isNewCurrentEntity.value) return
  generalStore.setShowNoFeature(false)
  generalStore.setShowConnectionPopup(false)
  if (currentEntity.value) {
    emit('updateConnection', currentEntity.value)
  } else {
    console.warn('No current entity selected for connection update.')
  }
}

watch(
  () => currentEntity.value,
  (newVal) => {
    if (!newVal) {
      hasNewCurrentEntity.value = false
      return
    }
    const pathIds = props.selectedItem?.entityConnectionPath ?? []
    const isInPath = pathIds.includes(newVal.id)
    if (!isInPath) {
      hasNewCurrentEntity.value = true
    }
  },
  { immediate: true }
)
onUnmounted(() => {
  hasNewCurrentEntity.value = false
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.popup-body {
  position: fixed;
  top: 80px;
  right: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 16px;
  background: black;
  z-index: 1000;
  color: white;
}

.popup-buttons {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
.button {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: white;
  height: unset;
}
.button-primary {
  background-color: variables.$color-primary;
  color: white;
  height: unset;
}
.disabled {
  pointer-events: none;
  color: #1a1a1a;
  background-color: #e0e0e0;
}
.popup-text {
  color: white;
  @include font-mixins.desktop-bold-label;
  width: 240px;
}
</style>
